import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../utils/api';
import OnboardingTour from './OnboardingTour';
import MobileOnboardingTour from './MobileOnboardingTour';
import OnboardingChecklist from './OnboardingChecklist';
import SupportModal from './SupportModal';
import SystemMessageModal from './SystemMessageModal';
import MobileBottomNav from './MobileBottomNav';
import { useTheme } from '../context/ThemeContext';
import {
  Settings, LogOut, Globe, Sun, Moon, HelpCircle, X,
} from 'lucide-react';
import SidebarNav from './layout/SidebarNav';
import AppHeader from './layout/AppHeader';
import useIsMobile from '../utils/useIsMobile';
import { LiveProvider } from '../context/LiveContext';
import CopilotDrawer from '../copilot/CopilotDrawer';
import ErrorBoundary from './ErrorBoundary';
import { AttachToProjectHost } from './common/AttachToProject';

// iter 214 — Layout.js Phase 1 refactor: 791 → ~210 sor (-74%).
// SidebarNav extracted to `./layout/SidebarNav.jsx` (282 lines + navGroups data).
// AppHeader  extracted to `./layout/AppHeader.jsx`  (~135 lines).
// Visual JSX, classNames, inline styles, data-testids — all preserved 1:1.

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [customLogo, setCustomLogo] = React.useState(null);
  const [designColors, setDesignColors] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [supportOpen, setSupportOpen] = React.useState(false);

  const { isMobile, isMobileDevice, isTouchDevice } = useIsMobile(768);

  // Apply body classes for global CSS targeting + one-time toast
  React.useEffect(() => {
    const body = document.body;
    if (!body) return undefined;
    body.classList.toggle('is-mobile', isMobile);
    body.classList.toggle('is-mobile-device', isMobileDevice);
    body.classList.toggle('is-touch-device', isTouchDevice);
    if (isMobileDevice && !sessionStorage.getItem('mobile-detected-toast')) {
      try {
        sessionStorage.setItem('mobile-detected-toast', '1');
        import('sonner').then(({ toast }) => {
          toast.success(
            i18n.language === 'hu'
              ? 'Mobilnézet aktiválva — érintésre optimalizálva 📱'
              : 'Mobilný režim aktivovaný — optimalizované pre dotyk 📱',
            { duration: 2800, position: 'top-center' }
          );
        }).catch(() => { /* ignore */ });
      } catch { /* ignore */ }
    }
    return () => {
      body.classList.remove('is-mobile', 'is-mobile-device', 'is-touch-device');
    };
  }, [isMobile, isMobileDevice, isTouchDevice, i18n.language]);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const DESIGN_VARS = React.useMemo(() => ['--primary', '--primary-foreground', '--accent-blue', '--background-base', '--background-surface', '--border', '--text-primary', '--text-secondary', '--sidebar-bg', '--sidebar-text', '--primary-hover', '--card-radius'], []);
  const applyDesignColors = React.useCallback((colors) => {
    const root = document.documentElement;
    // iter 361 — a branding-színek csak világos módban élnek: sötét módban a tokenek maradnak (fekete #0A0A0A primary + sötét előtér = olvashatatlan)
    if (!colors || root.classList.contains('dark')) { DESIGN_VARS.forEach((v) => root.style.removeProperty(v)); return; }
    // Only set CSS variables for fields that are actually present in the payload —
    // never override sidebar_* defaults from index.css with white fallbacks (would
    // cause white-on-white invisible nav text). iter 215 fix.
    const map = {
      primary: '--primary',
      accent: '--accent-blue',
      bg: '--background-base',
      surface: '--background-surface',
      border: '--border',
      text_primary: '--text-primary',
      text_secondary: '--text-secondary',
      sidebar_bg: '--sidebar-bg',
      sidebar_text: '--sidebar-text',
      // iter 310 — az aktív menüpont stílusát a dizájn-rendszer adja (réz jelölő), branding nem írja felül
    };
    Object.entries(map).forEach(([k, v]) => {
      const val = colors[k];
      if (val) root.style.setProperty(v, val);
    });
    // iter 215 — Auto-adjust sidebar overlay colors based on sidebar bg luminance.
    // Prevents white-on-white nav text when branding sets a light sidebar_bg.
    if (colors.sidebar_bg) {
      const hx = colors.sidebar_bg.replace('#', '');
      if (hx.length === 6) {
        const r = parseInt(hx.slice(0, 2), 16);
        const g = parseInt(hx.slice(2, 4), 16);
        const b = parseInt(hx.slice(4, 6), 16);
        const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        const isLight = lum > 0.55;
        root.style.setProperty('--sidebar-overlay', isLight ? 'rgba(15,23,42,0.05)' : 'rgba(255,255,255,0.06)');
        root.style.setProperty('--sidebar-overlay-strong', isLight ? 'rgba(15,23,42,0.10)' : 'rgba(255,255,255,0.14)');
        root.style.setProperty('--sidebar-border', isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.06)');
        root.style.setProperty('--sidebar-label-shadow', isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.25)');
        // Auto-correct contrast: if the branded sidebar_text would be invisible
        // (white on white / black on black), force a contrast-safe fallback.
        const isTextInvisible = (txt) => {
          if (!txt) return true;
          const c = txt.replace('#', '');
          if (c.length !== 6) return false;
          const tr = parseInt(c.slice(0, 2), 16);
          const tg = parseInt(c.slice(2, 4), 16);
          const tb = parseInt(c.slice(4, 6), 16);
          const tlum = (0.2126 * tr + 0.7152 * tg + 0.0722 * tb) / 255;
          // contrast ratio approximation — < 1.5 means invisible
          const ratio = isLight ? (1 / Math.max(0.01, tlum)) : (tlum / 0.05);
          return ratio < 1.6;
        };
        if (!colors.sidebar_text || isTextInvisible(colors.sidebar_text)) {
          root.style.setProperty('--sidebar-text', isLight ? '#475569' : '#B4BACA');
        }
        // User-name pill text needs a strong-contrast token. On a light sidebar
        // the "active text" (white) would also be invisible inside the overlay
        // pill — flip it to text-primary in that case.
        if (isLight) {
          // sidebar-active-text used by NavRow's active state stays white (active
          // rows have copper gradient bg). But the user-name pill uses
          // --sidebar-active-text directly, so we shadow that with a strong dark.
          root.style.setProperty('--sidebar-strong-text', '#0F1629');
        } else {
          root.style.setProperty('--sidebar-strong-text', '#FFFFFF');
        }
      }
    }
    if (colors.primary) {
      const hex = colors.primary.replace('#', '');
      const pr = parseInt(hex.slice(0, 2), 16), pg = parseInt(hex.slice(2, 4), 16), pb = parseInt(hex.slice(4, 6), 16);
      const r = Math.min(255, pr + 40);
      const g = Math.min(255, pg + 40);
      const b = Math.min(255, pb + 40);
      root.style.setProperty('--primary-hover', `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`);
      // előtér automatikusan: sötét primary → fehér, világos primary → sötét betű (kontraszt-garancia)
      root.style.setProperty('--primary-foreground', (0.2126 * pr + 0.7152 * pg + 0.0722 * pb) / 255 > 0.6 ? '#0F1629' : '#FFFFFF');
    }
    if (colors.card_radius) root.style.setProperty('--card-radius', colors.card_radius);
  }, [DESIGN_VARS]);

  React.useEffect(() => { applyDesignColors(designColors); }, [theme, designColors, applyDesignColors]);

  const fetchBranding = React.useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/settings/branding`, { withCredentials: true });
      if (resp.data?.logo_data) setCustomLogo(resp.data.logo_data);
      else setCustomLogo(null);
      if (resp.data?.design_colors) {
        setDesignColors(resp.data.design_colors);
      }
    } catch { /* */ }
  }, [applyDesignColors]);

  React.useEffect(() => {
    fetchBranding();
    const handler = () => fetchBranding();
    window.addEventListener('branding-updated', handler);
    return () => window.removeEventListener('branding-updated', handler);
  }, [fetchBranding]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'hu' ? 'sk' : 'hu';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const handleLogout = async () => { await logout(); };

  return (
    <LiveProvider>
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)', overflowX: 'clip' }}>
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`w-72 h-screen fixed flex flex-col z-40 transition-transform duration-300 md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        style={{
          backgroundColor: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--sidebar-border)',
        }}
      >
        {/* Logo + quick controls */}
        <div className="px-5 py-5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {customLogo ? (
              <img src={customLogo} alt="Logo" className="h-9 w-auto object-contain" data-testid="sidebar-logo" />
            ) : (
              <img src="/acountix-logo.png?v=3" alt="Acountix" className="h-8 w-auto object-contain select-none" data-testid="sidebar-logo-default" />
            )}
          </div>
          <Link
            to="/settings"
            data-testid="sidebar-settings-quick"
            aria-label={t('nav.settings')}
            title={t('nav.settings')}
            className="hidden md:flex p-1.5 rounded-lg transition-all hover:rotate-45"
            style={{ color: 'var(--sidebar-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(217, 112, 54, 0.14)'; e.currentTarget.style.color = '#E88653'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--sidebar-text)'; }}
          >
            <Settings size={18} strokeWidth={2} />
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-close-btn"
            className="md:hidden p-1.5 rounded-lg hover:bg-white/5 ml-1"
            style={{ color: 'var(--sidebar-text)' }}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <SidebarNav location={location} t={t} />

        {/* Footer — theme + lang + user + logout */}
        <div className="p-3" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          <div className="flex items-center gap-2 mb-2" data-testid="sidebar-theme-controls">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors text-[13px] font-medium"
              style={{ color: 'var(--sidebar-text)', backgroundColor: 'var(--sidebar-overlay)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--sidebar-overlay-strong)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--sidebar-overlay)'; }}
            >
              <Globe size={15} strokeWidth={1.75} />
              <span>{i18n.language === 'hu' ? 'Magyar' : 'Slovenčina'}</span>
            </button>
            <button
              onClick={toggleTheme}
              data-testid="sidebar-theme-toggle"
              className="flex items-center justify-center rounded-xl transition-colors"
              style={{
                width: 38, height: 38,
                background: 'var(--sidebar-overlay)',
                color: 'var(--sidebar-text)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--sidebar-overlay-strong)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--sidebar-overlay)'; }}
              title={theme === 'light' ? 'Sötét mód' : 'Világos mód'}
              aria-label="Téma váltó"
            >
              {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
            </button>
          </div>

          <button
            onClick={() => setSupportOpen(true)}
            data-testid="support-sidebar-btn"
            className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-colors mb-2 text-sm font-medium"
            style={{ color: 'var(--link-copper)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(217, 112, 54, 0.14)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <HelpCircle size={16} strokeWidth={1.9} />
            <span>{t('nav.support')}</span>
          </button>

          <div className="px-3.5 py-2.5 rounded-xl mb-2" style={{ backgroundColor: 'var(--sidebar-overlay)' }}>
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--sidebar-strong-text, var(--sidebar-active-text))' }}>{user?.name}</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--sidebar-text)', opacity: 0.75 }}>{t(`auth.${user?.role}`)}</p>
          </div>

          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-colors text-sm font-medium"
            style={{ color: 'var(--link-danger)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <LogOut size={16} strokeWidth={1.75} />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-72 min-h-screen">
        <AppHeader
          onMobileMenuOpen={() => setMobileOpen(true)}
          onSupportOpen={() => setSupportOpen(true)}
          onLogout={handleLogout}
          theme={theme}
          toggleTheme={toggleTheme}
          location={location}
          t={t}
        />
        <div className="p-4 md:p-8 pb-24 md:pb-8 animate-fade-in" onAnimationEnd={(e) => { if (e.target === e.currentTarget) e.currentTarget.style.animation = 'none'; }}>
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </div>
      </main>

      <MobileBottomNav onMoreClick={() => setMobileOpen(true)} />
      <OnboardingTour />
      <MobileOnboardingTour />
      <OnboardingChecklist />
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
      <SystemMessageModal />
      <CopilotDrawer />
      <AttachToProjectHost />
    </div>
    </LiveProvider>
  );
}
